import React, { useState } from 'react';
import { useNavigate, Link as RouterLink, Navigate } from 'react-router-dom';
import {
  Box, TextField, Button, Typography, Alert, CircularProgress, Link, InputAdornment, IconButton, Divider,
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { useAuth } from '../contexts/AuthContext';
import { brandColors, brandCss } from '../theme/appTheme';
import { authPrimaryButtonSx } from '../theme/formStyles';
import { AuthCoverLayout, authFont } from '../components/auth/AuthCoverLayout';
import { LOGIN_COVER } from '../components/auth/authCoverContent';
import { authTextFieldSx } from '../components/auth/authFieldStyles';

const authBtnSx = {
  ...authPrimaryButtonSx,
  fontFamily: authFont.family,
  borderRadius: '4px',
};

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const { login, loading, token, authReady, isSuperAdmin } = useAuth();
  const navigate = useNavigate();

  if (authReady && token) {
    return <Navigate to={isSuperAdmin ? '/super-admin' : '/dashboard'} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await login(email.trim().toLowerCase(), password);
      const savedUser = JSON.parse(localStorage.getItem('user') || '{}');
      navigate(savedUser.roleType === 'super_admin' ? '/super-admin' : '/dashboard');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Invalid username or password.');
    }
  };

  return (
    <AuthCoverLayout cover={LOGIN_COVER}>
      <Typography
        sx={{
          ...authFont.heading,
          fontSize: '1.65rem',
          color: brandColors.textDark,
          mb: 0.5,
          textAlign: 'left',
        }}
      >
        Sign In
      </Typography>
      <Typography
        sx={{
          ...authFont.body,
          fontSize: 14,
          color: brandColors.textMuted,
          mb: 2.75,
          textAlign: 'left',
        }}
      >
        Enter your email and password to continue
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, fontSize: 13, borderRadius: 2.5 }}>
          {error}
        </Alert>
      )}

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}
      >
        <TextField
          label="Email Address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          required
          autoComplete="username"
          autoFocus
          placeholder="you@company.com"
          sx={authTextFieldSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailOutlinedIcon sx={{ fontSize: 20, color: brandColors.textMuted }} />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          required
          autoComplete="current-password"
          placeholder="Enter your password"
          sx={authTextFieldSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon sx={{ fontSize: 20, color: brandColors.textMuted }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password"
                  onClick={() => setShowPassword((v) => !v)}
                  edge="end"
                  size="small"
                >
                  {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -0.75 }}>
          <Link
            component={RouterLink}
            to="/forgot-password"
            underline="hover"
            sx={{
              color: brandCss.primary,
              fontWeight: 600,
              fontSize: 13,
              fontFamily: authFont.family,
            }}
          >
            Forgot password?
          </Link>
        </Box>
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={loading}
          fullWidth
          endIcon={!loading ? <ArrowForwardRoundedIcon /> : undefined}
          sx={{
            ...authBtnSx,
            borderRadius: '4px',
            mt: 0.25,
          }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : 'Sign In'}
        </Button>
      </Box>

      <Divider
        sx={{
          my: 2.75,
          color: brandColors.textMuted,
          fontSize: 12,
          '&::before, &::after': { borderColor: brandColors.border },
        }}
      >
        or
      </Divider>

      <Button
        component={RouterLink}
        to="/register"
        variant="outlined"
        fullWidth
        size="large"
        startIcon={<PersonAddAlt1OutlinedIcon />}
        sx={{
          py: 1.4,
          height: 48,
          borderRadius: '4px',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: 14,
          fontFamily: authFont.family,
          lineHeight: '20px',
          borderColor: brandCss.primary,
          color: brandCss.primary,
          borderWidth: 1.5,
          '&:hover': {
            borderWidth: 1.5,
            borderColor: brandCss.primaryDark,
            bgcolor: 'rgba(98,33,214,0.04)',
          },
        }}
      >
        Create a new account
      </Button>

      <Box
        sx={{
          mt: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.75,
          color: brandColors.textMuted,
        }}
      >
        <VerifiedUserOutlinedIcon sx={{ fontSize: 16 }} />
        <Typography sx={{ fontSize: 12, fontFamily: authFont.family }}>
          Your data is safe and secure
        </Typography>
      </Box>
    </AuthCoverLayout>
  );
};
